<template>
  <div class="bonuses-page">
    <header class="bonuses-page__header">
      <div>
        <h1>{{ $t('bonuses.title') }}</h1>
        <p>{{ $t('bonuses.subtitle') }}</p>
      </div>
      <div class="bonuses-page__search-wrap">
        <i class="mdi mdi-magnify"></i>
        <input
          v-model="search"
          type="text"
          class="bonuses-page__search"
          :placeholder="$t('bonuses.searchPlaceholder')"
          @input="onSearchInput"
        >
      </div>
    </header>

    <section class="bonuses-page__settings">
      <div class="bonuses-page__settings-info">
        <div class="bonuses-page__settings-icon">
          <i :class="bonusType === 'n_bonus' ? 'mdi mdi-shopping-outline' : 'mdi mdi-percent-outline'"></i>
        </div>
        <div>
          <strong>{{ bonusType === 'n_bonus' ? $t('bonuses.nPurchaseTitle') : $t('bonuses.accrualRate') }}</strong>
          <p v-if="bonusType === 'n_bonus'">
            {{ $t('bonuses.nPurchaseText', { count: Number(purchaseCount) || 0, value: Number(bonusValue) || 0 }) }}
          </p>
          <p v-else>{{ $t('bonuses.accrualText', { value: Number(bonusValue) || 0 }) }}</p>
        </div>
      </div>

      <div class="bonuses-page__settings-form">
        <label class="bonuses-page__mode">
          <span>{{ $t('bonuses.mode') }}</span>
          <select v-model="bonusType">
            <option value="bonus">{{ $t('bonuses.modePercent') }}</option>
            <option value="n_bonus">{{ $t('bonuses.modeNPurchase') }}</option>
          </select>
        </label>
        <label v-if="bonusType === 'bonus'">
          <span>{{ $t('bonuses.percent') }}</span>
          <input
            v-model="bonusValue"
            type="number"
            min="0"
            max="100"
            step="0.1"
            inputmode="decimal"
            placeholder="5"
          >
        </label>
        <template v-else>
          <label>
            <span>{{ $t('bonuses.purchasesCount') }}</span>
            <input v-model="purchaseCount" type="number" min="1" step="1" inputmode="numeric" placeholder="5">
          </label>
          <label>
            <span>{{ $t('bonuses.rewardAmount') }}</span>
            <input v-model="bonusValue" type="number" min="1" step="1" inputmode="decimal" placeholder="500">
          </label>
        </template>
        <button
          type="button"
          class="bonuses-page__save"
          :disabled="settingsSaving"
          @click="saveSettings"
        >
          {{ settingsSaving ? $t('bonuses.saving') : $t('bonuses.save') }}
        </button>
      </div>
    </section>

    <section class="bonuses-page__kpi">
      <article class="kpi">
        <span>{{ $t('bonuses.clients') }}</span>
        <strong>{{ total }}</strong>
      </article>
      <article class="kpi kpi--accent">
        <span>{{ $t('bonuses.totalBonuses') }}</span>
        <strong>{{ formatMoney(totalBonus) }}</strong>
      </article>
      <article class="kpi">
        <span>{{ $t('bonuses.now') }}</span>
        <strong>{{ bonusType === 'n_bonus' ? `${Number(purchaseCount) || 0} ×` : `${Number(bonusValue) || 0}%` }}</strong>
      </article>
    </section>

    <div v-if="loading" class="bonuses-page__loading">
      <i class="mdi mdi-loading mdi-spin"></i>
      <span>{{ $t('bonuses.loading') }}</span>
    </div>

    <div v-else-if="clients.length" class="bonuses-page__list">
      <article v-for="client in clients" :key="client.id" class="bonus-card">
        <div class="bonus-card__icon">
          <i class="mdi mdi-account-star-outline"></i>
        </div>
        <div class="bonus-card__body">
          <h3>{{ client.name || $t('bonuses.unnamed') }}</h3>
          <p>
            {{ formatPhone(client.phone) }}
            <span v-if="client.card_number"> · {{ $t('bonuses.card') }} {{ client.card_number }}</span>
          </p>
        </div>
        <div class="bonus-card__bonus">
          <span>{{ $t('bonuses.bonus') }}</span>
          <strong>{{ formatMoney(client.bonus) }}</strong>
        </div>
      </article>
    </div>

    <div v-else class="bonuses-page__empty">
      <i class="mdi mdi-gift-outline"></i>
      <p>{{ search ? $t('bonuses.nothingFound') : $t('bonuses.noClients') }}</p>
      <span>
        {{ search
          ? $t('bonuses.tryAnother')
          : $t('bonuses.clientsAppear') }}
      </span>
    </div>

    <div v-if="pageCount > 1" class="bonuses-page__pagination">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="5"
        color="#0b4f8a"
      />
    </div>
  </div>
</template>

<script>
import { getBonusClients, getBonusSettings, updateBonusSettings } from '@/api/bonuses'
import { notify, notifyError } from '@/utils/notify'

const PER_PAGE = 12

export default {
  name: 'BonusClientsControl',
  data() {
    return {
      loading: false,
      clients: [],
      search: '',
      page: 1,
      pageCount: 0,
      total: 0,
      totalBonus: 0,
      searchTimer: null,
      bonusValue: 5,
      bonusType: 'bonus',
      purchaseCount: 5,
      settingsSaving: false,
    }
  },
  watch: {
    page() {
      this.load()
    },
  },
  mounted() {
    this.loadSettings()
    this.load()
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    formatMoney(value) {
      const amount = Number(value) || 0
      return `${new Intl.NumberFormat(this.$i18n.locale === 'kk' ? 'kk-KZ' : 'ru-RU').format(amount)} ₸`
    },
    formatPhone(phone) {
      const digits = String(phone || '').replace(/\D/g, '')
      if (digits.length !== 11) return phone || '—'
      return `${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        if (this.page !== 1) {
          this.page = 1
        } else {
          this.load()
        }
      }, 300)
    },
    async loadSettings() {
      try {
        const data = await getBonusSettings()
        this.bonusValue = data.bonus_value != null ? data.bonus_value : 5
        this.bonusType = data.type === 'n_bonus' ? 'n_bonus' : 'bonus'
        this.purchaseCount = data.count_value != null ? data.count_value : 5
      } catch (error) {
        notifyError(error)
      }
    },
    async saveSettings() {
      const value = Number(this.bonusValue)
      const count = Number(this.purchaseCount)
      if (
        Number.isNaN(value) ||
        (this.bonusType === 'bonus' && (value < 0 || value > 100)) ||
        (this.bonusType === 'n_bonus' && (value <= 0 || !Number.isInteger(count) || count < 1))
      ) {
        notify(this.$t(this.bonusType === 'n_bonus' ? 'bonuses.nPurchaseRange' : 'bonuses.percentRange'), 'warning')
        return
      }

      this.settingsSaving = true
      try {
        const data = await updateBonusSettings({
          bonus_value: value,
          count_value: this.bonusType === 'n_bonus' ? count : null,
          type: this.bonusType,
        })
        this.bonusValue = data.settings ? data.settings.bonus_value : value
        this.purchaseCount = data.settings && data.settings.count_value ? data.settings.count_value : count
        notify(data.message || this.$t('bonuses.settingsSaved'))
      } catch (error) {
        notifyError(error)
      } finally {
        this.settingsSaving = false
      }
    },
    async load() {
      this.loading = true
      try {
        const data = await getBonusClients({
          page: this.page,
          per_page: PER_PAGE,
          search: this.search.trim() || undefined,
        })
        this.clients = Array.isArray(data.data) ? data.data : []
        this.pageCount = Number(data.last_page) || 0
        this.total = Number(data.total) || 0
        this.totalBonus = Number(data.total_bonus) || 0
      } catch (error) {
        notifyError(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
$ink: #152033;
$muted: #6b7a90;
$accent: #0b4f8a;
$line: #d9e2ec;

.bonuses-page {
  padding: 28px 32px 40px;
  color: $ink;
}

.bonuses-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: $muted;
    font-size: 14px;
  }
}

.bonuses-page__search-wrap {
  position: relative;
  min-width: 280px;
  max-width: 360px;
  width: 100%;

  .mdi {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #9aa8b8;
    font-size: 20px;
  }
}

.bonuses-page__search {
  width: 100%;
  padding: 12px 14px 12px 42px;
  border: 1px solid $line;
  border-radius: 12px;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: $ink;
  outline: none;

  &:focus {
    border-color: $accent;
    box-shadow: 0 0 0 3px rgba(11, 79, 138, 0.1);
  }
}

.bonuses-page__settings {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid $line;
  border-radius: 16px;
}

.bonuses-page__settings-info {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;

  strong {
    display: block;
    font-size: 15px;
    font-weight: 750;
  }

  p {
    margin: 4px 0 0;
    color: $muted;
    font-size: 13.5px;
    line-height: 1.4;

    b {
      color: $accent;
    }
  }
}

.bonuses-page__settings-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: $accent;

  .mdi {
    font-size: 22px;
  }
}

.bonuses-page__settings-form {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-shrink: 0;

  label {
    display: block;

    span {
      display: block;
      margin-bottom: 6px;
      font-size: 12px;
      font-weight: 700;
      color: $muted;
    }

    input,
    select {
      width: 120px;
      padding: 11px 12px;
      border: 1.5px solid $line;
      border-radius: 12px;
      background: #fbfdff;
      font-size: 16px;
      font-weight: 750;
      color: $ink;
      outline: none;

      &:focus {
        border-color: $accent;
        box-shadow: 0 0 0 3px rgba(11, 79, 138, 0.1);
      }
    }
  }
}

.bonuses-page__mode select {
  width: 190px !important;
  cursor: pointer;
}

.bonuses-page__save {
  padding: 12px 16px;
  border: 0;
  border-radius: 12px;
  background: $accent;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #093f6d;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
}

.bonuses-page__kpi {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 220px));
  gap: 12px;
  margin-bottom: 20px;
}

.kpi {
  padding: 14px 16px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid $line;

  span {
    display: block;
    margin-bottom: 6px;
    color: $muted;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  strong {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  &--accent strong {
    color: $accent;
  }
}

.bonuses-page__loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 40px 0;
  color: $muted;

  .mdi {
    font-size: 22px;
  }
}

.bonuses-page__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bonus-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid $line;
  border-radius: 16px;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: #b7c6d6;
    transform: translateY(-1px);
  }
}

.bonus-card__icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: $accent;

  .mdi {
    font-size: 22px;
  }
}

.bonus-card__body {
  flex: 1;
  min-width: 0;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 750;
  }

  p {
    margin: 4px 0 0;
    color: $muted;
    font-size: 13px;
  }
}

.bonus-card__bonus {
  text-align: right;
  flex-shrink: 0;

  span {
    display: block;
    margin-bottom: 2px;
    color: $muted;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  strong {
    font-size: 18px;
    font-weight: 800;
    color: $accent;
  }
}

.bonuses-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  padding: 48px;
  text-align: center;
  color: $muted;
  border: 1px dashed $line;
  border-radius: 16px;
  background: #fff;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: $ink;
    font-size: 16px;
    font-weight: 700;
  }

  span {
    font-size: 13px;
  }
}

.bonuses-page__pagination {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

@media (max-width: 900px) {
  .bonuses-page__settings {
    flex-direction: column;
    align-items: stretch;
  }

  .bonuses-page__settings-form {
    justify-content: flex-start;
  }

  .bonuses-page__kpi {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

@media (max-width: 720px) {
  .bonuses-page {
    padding: 20px 16px 32px;
  }

  .bonuses-page__header {
    flex-direction: column;
  }

  .bonuses-page__search-wrap {
    max-width: none;
  }

  .bonuses-page__kpi {
    grid-template-columns: 1fr;
  }

  .bonus-card {
    flex-wrap: wrap;
  }

  .bonus-card__bonus {
    width: 100%;
    text-align: left;
    padding-top: 8px;
    border-top: 1px solid #eef2f6;
  }

  .bonuses-page__settings-form {
    flex-direction: column;
    align-items: stretch;

    label input,
    label select,
    .bonuses-page__save {
      width: 100%;
    }
  }
}
</style>
